import api from '../../utils/axios';
import { useBookingContext } from '../../context/BookingContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useBookingForm from '../../hooks/useBookingForm';
import { toUTCISOString } from '../../utils/DateUtil';

const CreateBooking = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { state, dispatch } = useBookingContext();
  const { users, vehicles, usersLoading, vehiclesLoading } = state;

  const {
    form,
    setForm,
    loading,
    setLoading,
    error,
    setError,
    handleChange,
    handleOfficeChange,
    addPassenger,
    removePassenger,
  } = useBookingForm(null, dispatch); // dispatch from context

   const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.userPassengers.length === 0) {
      alert("Please select at least one passenger before submitting.");
      return;
    }
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...form,
        nonUserPassengers: form.nonUserPassengers
          .split(',')
          .map(p => p.trim())
          .filter(p => p !== ''),
        startDate: toUTCISOString(form.startDate), // local → UTC
        endDate:   toUTCISOString(form.endDate),
      };

      await api.post(`/api/car-bookings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/bookings');
    } catch (err) {
      console.error(err);
      setError('Failed to create booking.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-6 text-start">
          <h2 className="mb-4">Create Car Booking</h2>

          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleSubmit} className="mt-3">
            {/* Date/Time */}
            <div className="row mb-3">
              <div className="col">
                <label className="form-label">Start Date & Time</label>
                <input
                  type="datetime-local"
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="col">
                <label className="form-label">End Date & Time</label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={form.endDate}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>
            </div>

            {/* Purpose/Destination */}
            <div className="mb-3">
              <label className="form-label">Purpose</label>
              <input type="text" name="purpose" value={form.purpose} onChange={handleChange} className="form-control" required />
            </div>

            <div className="mb-3">
              <label className="form-label">Destination</label>
              <input type="text" name="destination" value={form.destination} onChange={handleChange} className="form-control" required />
            </div>

            {/* Office */}
            <div className="mb-3">
              <label className="form-label">Office Location</label>
              <select name="office" value={form.office} onChange={handleOfficeChange} className="form-select" required>
                <option value="">-- Select Office --</option>
                <option value="HQ">HQ</option>
                <option value="SITE">Site</option>
              </select>
            </div>

            {/* Preferred Vehicle */}
            <div className="mb-3">
              <label className="form-label">Preferred Vehicle</label>
              <select
                name="preferredVehicle"
                value={form.preferredVehicle}
                onChange={handleChange}
                className="form-select"
                disabled={!vehicles.length}
                required
              >
                <option value="">-- Select Vehicle --</option>
                {vehicles.map((v) => (
                  <option key={v._id} value={v._id}>
                    {v.name} ({v.plateNumber})
                  </option>
                ))}
              </select>
              {vehiclesLoading && <small className="text-muted">Loading vehicles...</small>}
            </div>

            {/* Approver */}
            <div className="mb-3">
              <label className="form-label">Approver <span className='text-danger'>(Line Manager or Deputy Director)</span></label>
              <select name="approver" value={form.approver} onChange={handleChange} className="form-select" required>
                <option value="">-- Select Approver --</option>
                {users.filter(u => u._id !== user?._id).map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
              {usersLoading && <small className="text-muted">Loading Approvers...</small>}
            </div>

            {/* Add Passengers */}
            <div className="mb-3">
              <label className="form-label">Add Passenger</label>
              <div className="input-group">
                <select
                  className="form-select"
                  value={form.passengerToAdd || ''}
                  onChange={(e) => setForm({ ...form, passengerToAdd: e.target.value })}
                >
                  <option value="">-- Select a passenger --</option>
                  {users.map(u => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
                {usersLoading && <small className="text-muted">Loading Passengers...</small>}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addPassenger}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Display Passengers */}
            {form.userPassengers.length > 0 && (
              <div className="mb-3">
                <label className="form-label">Selected Passengers:</label>
                <ul className="list-group">
                  {form.userPassengers.map((id) => {
                    const u = users.find(user => user._id === id);
                    return (
                      <li key={id} className="list-group-item d-flex justify-content-between align-items-center">
                        {u?.name || 'Unknown'}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={removePassenger}
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Non-user Passengers */}
            <div className="mb-3">
              <label className="form-label">Other Passengers (comma-separated)</label>
              <input
                type="text"
                name="nonUserPassengers"
                value={form.nonUserPassengers}
                onChange={handleChange}
                className="form-control"
                placeholder="John Doe, Jane Smith"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Booking'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBooking;
